/**
 * UserSessions.tsx — the per-row "Devices" expansion inside `UsersSection`
 * (Task 3, final task of the signed-in-devices work — spec 2026-09-11 §the
 * console). One clear job: fetch and render one staff member's signed-in
 * sessions over Task 2's HTTP surface (services/ops/src/routes/
 * user-sessions.ts, commit b9c58ab) and let an admin sign one out, or sign
 * out everywhere.
 *
 * MIRRORS UsersSection/LinesSection'S OWN CONVENTIONS: admin guard lives one
 * level up (this component only ever mounts inside the already admin-gated
 * Users pane — `UsersSection.tsx`'s own header comment explains why that
 * gate is safe to rely on), `useStore` for `pushToast`, a load-error state
 * with the server's own honest message.
 *
 * REVOKE MEANS "CAN'T RENEW AGAIN," NOT "SIGNED OUT THIS INSTANT" —
 * user-sessions.ts's own header comment. Nothing here claims the stronger
 * thing: copy says "sign out," matching the button a person expects to
 * click, but never implies an already-live access token vanishes mid-tab.
 *
 * THE REVOKE-ALL COUNT MISMATCH IS DELIBERATE, NOT A BUG TO PAPER OVER. The
 * confirm prompt uses the VISIBLE count (this component's own list) because
 * that is the number a person can see and reason about; the success toast
 * reports the SERVER's count because `ops.revokeAllSessions` also flips
 * expired-but-unrevoked rows this list never displayed (Task 2 review's
 * carried ruling). The two numbers can legitimately differ — see the toast
 * call below for the one-line note at the point that could look like a
 * mismatch bug to a future reader.
 */
import { useEffect, useState } from 'react'
import { ops } from '../../lib/ops'
import type { StaffSession } from '../../lib/ops'
import { useStore } from '../../store'
import { Button } from '../ui'

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

/**
 * A small, deliberately crude browser label — display-only. The spec this
 * implements is explicit that this must never be trusted for anything but a
 * hint in a table cell (a user agent string is trivially spoofable); the raw
 * value is still carried in `title` for whoever wants to look closer.
 */
export function uaLabel(ua: string | null): string {
  if (ua === null) return 'Unknown browser'
  if (ua.includes('iPhone')) return 'Safari on iPhone'
  if (ua.includes('iPad')) return 'Safari on iPad'
  if (ua.includes('Android')) return 'Browser on Android'
  if (ua.includes('Windows')) return 'Browser on Windows'
  if (ua.includes('Macintosh')) return 'Browser on Mac'
  return 'Unknown browser'
}

/**
 * `uncertainBefore` is the server's own migration-025 apply time
 * (services/ops/src/routes/user-sessions.ts's `getUncertainBefore`), not a
 * literal baked into this file — a literal drifts from reality the moment
 * the deploy date changes: land early and genuine same-day sign-ins get
 * mislabeled "before Sep 2026"; land late and backfilled rows between the
 * literal and the real apply time get false precision instead of the honest
 * label. See that migration's own header comment for the backfill this
 * exists to describe honestly.
 */
function createdAtLabel(createdAt: string, uncertainBefore: string): string {
  const ms = new Date(createdAt).getTime()
  if (ms < new Date(uncertainBefore).getTime()) return 'before Sep 2026'
  return DATE_FORMAT.format(new Date(ms))
}

function expiresLabel(expiresAt: string): string {
  return `Signs out ${DATE_FORMAT.format(new Date(expiresAt))} if unused`
}

/** "moments ago" / "Nm ago" / "Nh ago" / "Nd ago" — same shape as rail/format.ts's `agoLabel`, kept local so this file stays one self-contained job. */
function relativeTime(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (s < 60) return 'moments ago'
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

const LAST_ACTIVE_CAVEAT = 'Accurate to the last token rotation — up to one session length behind.'

export function UserSessions({ userId, userName }: { userId: string; userName: string }) {
  const pushToast = useStore((s) => s.pushToast)

  const [sessions, setSessions] = useState<StaffSession[] | null>(null)
  // The server's migration-025 apply time — see `createdAtLabel`'s own
  // comment for why this must come from the response, never a literal.
  const [uncertainBefore, setUncertainBefore] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Two-click arm/confirm gate for "sign out everywhere" — see this file's
  // own header comment on why the confirm copy uses the visible count.
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setSessions(null)
    setUncertainBefore(null)
    setLoadError(null)
    setArmed(false)
    ops
      .userSessions(userId)
      .then(({ sessions: list, uncertainBefore: before }) => {
        if (cancelled) return
        setSessions(list)
        setUncertainBefore(before)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : `Could not load sessions for ${userName}.`)
      })
    return () => {
      cancelled = true
    }
  }, [userId, userName])

  async function signOut(sessionId: string) {
    try {
      await ops.revokeSession(userId, sessionId)
      setSessions((prev) => (prev ? prev.filter((s) => s.id !== sessionId) : prev))
    } catch (err) {
      pushToast({
        text: err instanceof Error ? err.message : `Could not sign out that session for ${userName}.`,
        tone: 'bad',
      })
    }
  }

  async function signOutAll() {
    if (!armed) {
      setArmed(true)
      return
    }
    setArmed(false)
    try {
      const revoked = await ops.revokeAllSessions(userId)
      setSessions([])
      // `revoked` is the SERVER's count, not `sessions.length` — it also
      // flips expired-but-unrevoked rows this list never showed (Task 2
      // review's carried ruling), so the two numbers can legitimately
      // differ. Reporting the server's own number here, not the visible
      // count the confirm prompt used, is deliberate.
      pushToast({ text: `Signed out ${revoked} sessions.`, tone: 'ok' })
    } catch (err) {
      pushToast({
        text: err instanceof Error ? err.message : `Could not sign out every session for ${userName}.`,
        tone: 'bad',
      })
    }
  }

  return (
    <div data-testid={`user-sessions-${userId}`} className="border-t border-subtle bg-sunken px-4 py-3">
      {loadError ? (
        <p className="text-[13px] text-bad" data-testid="sessions-load-error">
          {loadError}
        </p>
      ) : sessions === null || uncertainBefore === null ? (
        <p className="text-[13px] text-mid" data-testid="sessions-loading">
          Loading…
        </p>
      ) : sessions.length === 0 ? (
        <p className="text-[13px] text-mid" data-testid="sessions-empty">
          No active sessions.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="overflow-x-auto" data-testid="sessions-table">
            <table className="w-full min-w-[560px] text-left text-[12.5px]">
              <thead>
                <tr className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-lo">
                  <th className="pb-1.5 pr-3 font-semibold">Browser</th>
                  <th className="pb-1.5 pr-3 font-semibold">Signed in</th>
                  <th className="pb-1.5 pr-3 font-semibold">Last active</th>
                  <th className="pb-1.5 pr-3 font-semibold">Expires</th>
                  <th className="pb-1.5 font-semibold">&nbsp;</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-subtle">
                {sessions.map((s) => (
                  <tr key={s.id} data-testid={`sessions-row-${s.id}`}>
                    <td className="py-1.5 pr-3 text-hi" data-testid={`sessions-row-${s.id}-browser`} title={s.userAgent ?? undefined}>
                      {uaLabel(s.userAgent)}
                    </td>
                    <td className="py-1.5 pr-3 text-mid" data-testid={`sessions-row-${s.id}-created`}>
                      {createdAtLabel(s.createdAt, uncertainBefore)}
                    </td>
                    <td
                      className="py-1.5 pr-3 text-mid"
                      data-testid={`sessions-row-${s.id}-last-active`}
                      title={LAST_ACTIVE_CAVEAT}
                    >
                      {s.lastRotatedAt ? relativeTime(s.lastRotatedAt) : '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-mid" data-testid={`sessions-row-${s.id}-expires`}>
                      {expiresLabel(s.expiresAt)}
                    </td>
                    <td className="py-1.5">
                      <Button
                        type="button"
                        variant="danger"
                        className="whitespace-nowrap"
                        data-testid={`sessions-row-${s.id}-sign-out`}
                        onClick={() => void signOut(s.id)}
                      >
                        Sign out
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sessions.length > 1 ? (
            <div>
              <Button
                type="button"
                variant="danger"
                className="whitespace-nowrap"
                data-testid="sessions-sign-out-all"
                onClick={() => void signOutAll()}
              >
                {armed ? `Really sign out ${sessions.length} sessions?` : `Sign out all (${sessions.length})`}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
